import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    gender: '',
    address: '',
    experience: '',
    motivation: '',
    income_goal: '',
    marketing_channels: [] as string[],
    privacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    
    if (name === 'privacy') {
      setFormData(prev => ({ ...prev, privacy: checked }));
    } else if (name === 'marketing_channel') {
      setFormData(prev => ({
        ...prev,
        marketing_channels: checked
          ? [...prev.marketing_channels, value]
          : prev.marketing_channels.filter(item => item !== value)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    if (formData.motivation.length > 500) {
      alert('지원 동기는 500자 이내로 작성해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('age', formData.age);
      formBody.append('gender', formData.gender);
      formBody.append('address', formData.address);
      formBody.append('experience', formData.experience);
      formBody.append('motivation', formData.motivation);
      formBody.append('income_goal', formData.income_goal);
      formBody.append('marketing_channels', formData.marketing_channels.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d5mrpunpl7huldvcimhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          age: '',
          gender: '',
          address: '',
          experience: '',
          motivation: '',
          income_goal: '',
          marketing_channels: [],
          privacy: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToApply = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              DNA 유전자 검사 솔루션으로<br />
              <span className="text-yellow-300">연봉 1억 이상</span> 달성하세요
            </h1>
            <p className="text-lg lg:text-xl mb-10 text-indigo-100 max-w-2xl mx-auto">
              DNA 전문 교육과 독점 DB 제공으로<br />
              월 500만원 이상의 수익을 보장합니다
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="#apply" 
                onClick={scrollToApply}
                className="px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-semibold text-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                지금 바로 신청하기
              </a>
              <a 
                href="#benefits"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
              >
                혜택 자세히 보기
              </a>
            </div>
            <div className="flex flex-wrap gap-6 justify-center">
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-sm lg:text-base">DNA 전문기관 제휴</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-sm lg:text-base">대학교 강의 인증</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-sm lg:text-base">자격증 발급</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Income Stats Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-gray-800 mb-2">500만원 이상</div>
              <div className="text-gray-600 font-medium">월평균 수익</div>
            </div>
            <div className="bg-gradient-to-br from-pink-400 to-rose-400 rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-white mb-2">월 100만원</div>
              <div className="text-white font-medium">달성 목표</div>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-gray-800 mb-2">100만원</div>
              <div className="text-gray-600 font-medium">교육 마케팅 지원</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16 text-gray-900">
            우리가 제공하는 특별한 혜택
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '🧬',
                title: 'DNA 유전자 검사 DB 제공',
                description: '독점적인 질병 및 반려견 DNA 유전자 검사 데이터베이스를 통해 고객에게 맞춤형 보험 상품을 제안할 수 있습니다.'
              },
              {
                icon: '🎓',
                title: '전문 교육 및 자격증',
                description: 'DNA 전문 기관과 대학교 교육과정을 통한 체계적인 교육으로 전문 자격증을 발급받습니다.'
              },
              {
                icon: '📱',
                title: '온라인 마케팅 교육',
                description: 'SNS, 블로그, 광고 등 최신 온라인 마케팅    기법을 100% 무료로 교육 받습니다.'
              },
              {
                icon: '💼',
                title: '운영 시스템 지원',
                description: '고객 관리, 계약 관리, 수익 분석 등 모든 운영 시스템을 무료로 제공합니다.'
              },
              {
                icon: '📈',
                title: '지속적인 수익 모델',
                description: '한 번의 계약으로 지속적인 수수료 수익이 발생하는 안정적인 비즈니스 모델입니다.'
              },
              {
                icon: '🤝',
                title: '1:1 멘토링 지원',
                description: '경험 많은 선배 설계사의 1:1 멘토링으로 빠른 성공을 보장합니다.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100"
              >
                <div className="text-5xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16 text-gray-900">
            시작부터 성공까지, 4단계면 충분합니다
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { number: '1', title: '지원서 제출' },
              { number: '2', title: '교육 프로그램' },
              { number: '3', title: '마케팅 실전' },
              { number: '4', title: '수익 창출' }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4 shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16">
            이런 분들께 추천합니다
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '💰', title: '월 500만원 이상\n벌고 싶은 분' },
              { icon: '🎯', title: '연봉 1억 이상\n달성하고 싶은 분' },
              { icon: '🚀', title: '새로운 커리어를\n시작하고 싶은 분' },
              { icon: '⏰', title: '시간과 장소에\n구애받지 않는 분' },
              { icon: '🔍', title: '더이상 만날 고객이\n없는 분' },
              { icon: '🚫', title: '가족 지인에게\n영업하고 싶지 않은 분' }
            ].map((target, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all border border-white/20"
              >
                <div className="text-5xl mb-4">{target.icon}</div>
                <h3 className="text-lg font-semibold whitespace-pre-line">{target.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16 text-gray-900">
            먼저 시작한 분들의 성공 스토리
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                stars: 5,
                text: '3개월 만에 월 600만원 수익 달성! DNA 유전자 검사 DB 덕분에 고객 신뢰도가 엄청 높아졌어요.',
                author: '김○○',
                location: '서울 강남구',
                period: '6개월차'
              },
              {
                stars: 5,
                text: '처음엔 불안했지만 체계적인 교육과 마케팅 지원으로 첫 달부터 400만원 벌었습니다. 연봉 1억 목표가 현실이 되고 있어요!',
                author: '이○○',
                location: '경기 분당구',
                period: '1년차'
              },
              {
                stars: 5,
                text: '온라인 마케팅 교육이 정말 실전적이에요. SNS로만 월 500만원 넘게 벌고 있습니다.',
                author: '박○○',
                location: '부산 해운대구',
                period: '8개월차'
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              >
                <div className="text-yellow-400 text-2xl mb-4">
                  {'★'.repeat(testimonial.stars)}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="text-sm text-gray-600">
                  <strong className="text-gray-900">{testimonial.author}</strong> · {testimonial.location} · {testimonial.period}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-gray-900">
              지금 바로 신청하세요
            </h2>
            <p className="text-lg text-gray-600">
              월 500만원 이상, 연봉 1억 이상의 기회가 당신을 기다립니다
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
            <form id="applicationForm" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  이름 *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="홍길동"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="010-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-2">
                    나이 *
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                    placeholder="30"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="gender" className="block text-sm font-semibold text-gray-700 mb-2">
                    성별 *
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">선택하세요</option>
                    <option value="남성">남성</option>
                    <option value="여성">여성</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                  주소 *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="서울특별시 강남구"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                  보험 관련 경력
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">선택하세요</option>
                  <option value="없음">없음</option>
                  <option value="1년 미만">1년 미만</option>
                  <option value="1-3년">1-3년</option>
                  <option value="3-5년">3-5년</option>
                  <option value="5년 이상">5년 이상</option>
                </select>
              </div>

              <div>
                <label htmlFor="motivation" className="block text-sm font-semibold text-gray-700 mb-2">
                  지원 동기 * (최대 500자)
                </label>
                <textarea
                  id="motivation"
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="DNA 유전자 검사 기반 보험설계사로 지원하시는 이유를 작성해주세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.motivation.length}/500
                </div>
              </div>

              <div>
                <label htmlFor="income_goal" className="block text-sm font-semibold text-gray-700 mb-2">
                  수익 목표 *
                </label>
                <select
                  id="income_goal"
                  name="income_goal"
                  value={formData.income_goal}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">선택하세요</option>
                  <option value="월 300-500만원">월 300-500만원</option>
                  <option value="월 500-700만원">월 500-700만원</option>
                  <option value="월 700-1000만원">월 700-1000만원</option>
                  <option value="월 1000만원 이상">월 1000만원 이상</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  관심 마케팅 채널
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'SNS', label: 'SNS 마케팅 관심' },
                    { value: '블로그', label: '블로그 마케팅 관심' },
                    { value: '유튜브', label: '유튜브 마케팅 관심' },
                    { value: '유전자검사DB', label: '유전자 검사 DB 관심' }
                  ].map((channel) => (
                    <label key={channel.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="marketing_channel"
                        value={channel.value}
                        checked={formData.marketing_channels.includes(channel.value)}
                        onChange={handleCheckboxChange}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{channel.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    id="privacy"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={handleCheckboxChange}
                    required
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-1 cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    개인정보 수집 및 이용에 동의합니다 *
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-bold text-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '제출 중...' : '🚀 지금 바로 신청하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm text-center">
                  신청이 완료되었습니다! 영업일 기준 1-2일 내로 연락드리겠습니다.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm text-center">
                  신청 중 오류가 발생했습니다. 다시 시도해주세요.
                </div>
              )}
            </form>

            <div className="mt-6 text-center text-sm text-gray-500 space-y-1">
              <p>* 표시는 필수 입력 항목입니다</p>
              <p>신청하신 내용은 안전하게 보관되며, 영업일 기준 1-2일 내로 연락드립니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            지금이 바로 시작할 때입니다
          </h2>
          <p className="text-xl mb-10 text-indigo-100">
            월 500만원 이상, 연봉 1억 이상의 기회를 놓치지 마세요
          </p>
          <a 
            href="#apply" 
            onClick={scrollToApply}
            className="inline-block px-12 py-5 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 rounded-full font-bold text-xl hover:from-yellow-500 hover:to-orange-600 transition-all shadow-2xl hover:shadow-3xl whitespace-nowrap cursor-pointer"
          >
            지금 바로 신청하기
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <h4 className="text-white text-lg font-bold mb-4">DNA 보험설계사 모집</h4>
              <p className="text-sm leading-relaxed">유전자 검사 기반의 새로운 보험 패러다임</p>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-4">문의</h4>
              <p className="text-sm mb-2">Email: vlgs77@hanmail.net</p>
              <p className="text-sm">Tel: 010-2429-0259</p>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-4">운영시간</h4>
              <p className="text-sm mb-2">평일 09:00 - 18:00</p>
              <p className="text-sm">토/일/공휴일 휴무</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm">
              © 2026 DNA Insurance Recruitment. All rights reserved. · <a href="https://readdy.ai/?ref=logo" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
